/**
 * CMS слой — читает/пишет текстовые блоки в Blink DB
 * Никакого долгосрочного кеша — всегда свежие данные из БД.
 * Краткосрочный кеш (requestCache) живёт только внутри одного вызова
 * preloadPageCms, чтобы не делать дублированных запросов за одну загрузку.
 */

import { cmsBlocksDb } from './adminDb';
import { saveHeroTextsToCache } from './globalDataCache';

// ─── Per-request cache — очищается перед каждым preloadPageCms ───────────────
// Не сохраняется между посещениями страниц и языковыми переключениями

const cache = new Map<string, string>();

// In-flight deduplication — prevents 3 parallel calls for the same pageKey
// (one per language) from triggering 3 identical DB requests.
const preloadInFlight = new Map<string, Promise<void>>();

// Per-pageKey freshness — once we've fetched a page's CMS blocks, treat them
// as fresh for this many ms. Avoids re-fetching the same page within the same
// session when getHomeHeroData() and fetchHeroTexts() both want the data.
const PRELOAD_FRESH_MS = 5 * 60 * 1000; // 5 minutes
const preloadedAt = new Map<string, number>();

// ─── Event bus for instant UI updates after CMS save ─────────────────────────

type CmsUpdateListener = (pageKey: string, blockKey: string, lang: string, value: string) => void;
const updateListeners = new Set<CmsUpdateListener>();

export function onCmsUpdate(listener: CmsUpdateListener): () => void {
  updateListeners.add(listener);
  return () => updateListeners.delete(listener);
}

function notifyCmsUpdate(pageKey: string, blockKey: string, lang: string, value: string): void {
  for (const listener of updateListeners) {
    try { listener(pageKey, blockKey, lang, value); } catch { /* ignore */ }
  }
}

function cacheKey(pageKey: string, blockKey: string, lang: string): string {
  return `${pageKey}:${blockKey}:${lang}`;
}

function dbId(pageKey: string, blockKey: string, lang: string): string {
  return `${pageKey}__${blockKey}__${lang}`;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Получить CMS-значение.
 * Сначала из per-request кеша (заполняется preloadPageCms),
 * затем из DB, иначе fallbackValue.
 */
export async function getCms(
  pageKey: string,
  blockKey: string,
  lang: string,
  fallbackValue: string
): Promise<string> {
  const key = cacheKey(pageKey, blockKey, lang);
  if (cache.has(key)) return cache.get(key)!;

  // If a preload for this page is in flight, wait for it instead of issuing
  // a parallel DB request.
  const inFlight = preloadInFlight.get(pageKey);
  if (inFlight) {
    await inFlight;
    if (cache.has(key)) return cache.get(key)!;
    // Block genuinely absent — cache fallback to prevent repeated lookups.
    cache.set(key, fallbackValue);
    return fallbackValue;
  }

  // If this page was preloaded recently, the block is simply absent in DB —
  // skip the redundant network call.
  if (preloadedAt.has(pageKey)) {
    cache.set(key, fallbackValue);
    return fallbackValue;
  }

  // Cold path: trigger a full page preload (it'll cache all blocks at once),
  // then resolve from cache.
  try {
    await preloadPageCms(pageKey);
    if (cache.has(key)) return cache.get(key)!;
  } catch {
    // DB unavailable — fall through to fallback below
  }

  cache.set(key, fallbackValue);
  return fallbackValue;
}

/**
 * Получить CMS-значение синхронно (только из кеша текущей загрузки).
 */
export function getCmsSync(
  pageKey: string,
  blockKey: string,
  lang: string,
  fallbackValue: string
): string {
  const key = cacheKey(pageKey, blockKey, lang);
  return cache.has(key) ? cache.get(key)! : fallbackValue;
}

/**
 * Записать CMS-значение в DB и кеш.
 */
export async function setCms(
  pageKey: string,
  blockKey: string,
  lang: string,
  value: string
): Promise<void> {
  const key = cacheKey(pageKey, blockKey, lang);
  const id = dbId(pageKey, blockKey, lang);
  cache.set(key, value);
  notifyCmsUpdate(pageKey, blockKey, lang, value);

  // Update hero texts cache in localStorage immediately
  if (blockKey.startsWith('hero.')) {
    const existing = {
      eyebrow: cache.get(cacheKey(pageKey, 'hero.eyebrow', lang)) || '',
      title: cache.get(cacheKey(pageKey, 'hero.title', lang)) || '',
      subtitle: cache.get(cacheKey(pageKey, 'hero.subtitle', lang)) || '',
    };
    const updated = { ...existing, [blockKey.replace('hero.', '')]: value };
    saveHeroTextsToCache(pageKey, lang, updated);
  }

  await cmsBlocksDb.upsert({
    id,
    pageKey,
    blockKey,
    lang,
    contentJson: JSON.stringify(value),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Предзагрузить все блоки для страницы в кеш.
 * ВСЕГДА делает свежий запрос к БД (не кеширует между посещениями).
 */
export async function preloadPageCms(pageKey: string): Promise<void> {
  // 1. If we've fetched this page's blocks recently, skip the network call.
  const lastAt = preloadedAt.get(pageKey);
  if (lastAt && Date.now() - lastAt < PRELOAD_FRESH_MS) return;

  // 2. If a preload for this pageKey is already in flight, reuse it.
  const inFlight = preloadInFlight.get(pageKey);
  if (inFlight) return inFlight;

  const promise = (async () => {
    // Replace this page's cache entries with fresh data from DB.
    for (const k of cache.keys()) {
      if (k.startsWith(`${pageKey}:`)) cache.delete(k);
    }
    try {
      const result = await cmsBlocksDb.getByPage(pageKey);
      const blocks = result.ok ? result.data : [];
      for (const block of blocks) {
        if (!block?.blockKey || !block?.lang) continue;
        const key = cacheKey(block.pageKey, block.blockKey, block.lang);
        try {
          const parsed = JSON.parse(block.contentJson);
          cache.set(key, typeof parsed === 'string' ? parsed : parsed.text ?? '');
        } catch {
          cache.set(key, block.contentJson);
        }
      }
      preloadedAt.set(pageKey, Date.now());
    } catch {
      // fail silently — leave preloadedAt unset so we'll retry next time
    } finally {
      preloadInFlight.delete(pageKey);
    }
  })();

  preloadInFlight.set(pageKey, promise);
  return promise;
}

/**
 * Очистить кеш (например, после сохранения в админке).
 */
export function clearCmsCache(pageKey?: string, blockKey?: string, lang?: string): void {
  if (pageKey && blockKey && lang) {
    cache.delete(cacheKey(pageKey, blockKey, lang));
    preloadedAt.delete(pageKey);
  } else if (pageKey) {
    for (const k of cache.keys()) {
      if (k.startsWith(`${pageKey}:`)) cache.delete(k);
    }
    preloadedAt.delete(pageKey);
  } else {
    cache.clear();
    preloadedAt.clear();
  }
}

// ─── CMS Page definitions ─────────────────────────────────────────────────────

export type CmsBlockDef = {
  blockKey: string;
  label: string;
  multiline?: boolean;
  type?: 'text' | 'url';
};

export type CmsPageDef = {
  pageKey: string;
  label: string;
  blocks: CmsBlockDef[];
};

export const CMS_PAGES: CmsPageDef[] = [
  {
    pageKey: 'home',
    label: 'Главная',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
      { blockKey: 'hero.cta1.text', label: 'Hero — Кнопка 1: Текст' },
      { blockKey: 'hero.cta1.url', label: 'Hero — Кнопка 1: Ссылка', type: 'url' },
      { blockKey: 'hero.cta2.text', label: 'Hero — Кнопка 2: Текст' },
      { blockKey: 'hero.cta2.url', label: 'Hero — Кнопка 2: Ссылка', type: 'url' },
    ],
  },
  {
    pageKey: 'catalog',
    label: 'Каталог',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
    ],
  },
  {
    pageKey: 'portfolio',
    label: 'Портфолио',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
    ],
  },
  {
    pageKey: 'news',
    label: 'Новости',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
    ],
  },
  {
    pageKey: 'about',
    label: 'О заводе',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
    ],
  },
  {
    pageKey: 'wheretobuy',
    label: 'Где купить',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
    ],
  },
  {
    pageKey: 'becomedealer',
    label: 'Стать дилером',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
    ],
  },
  {
    pageKey: 'useful',
    label: 'Полезное',
    blocks: [
      { blockKey: 'hero.eyebrow', label: 'Hero — Надзаголовок' },
      { blockKey: 'hero.title', label: 'Hero — Заголовок' },
      { blockKey: 'hero.subtitle', label: 'Hero — Подзаголовок', multiline: true },
    ],
  },
];
